import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Bell, Check, ChevronRight, Loader2 } from "lucide-react";
import {
  useListNotifications,
  useMarkNotificationRead,
  getListNotificationsQueryKey,
  type Notification,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";

function timeAgo(iso: string | undefined): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

// Maps a notification to its in-portal destination. Import-sourced notifications
// drill through to the affected orders; the rest route by their `type`.
function linkForNotification(n: Notification): string | null {
  if (n.dataImportId != null) return `/orders?notification=${n.id}`;
  switch (n.type) {
    case "onboarding_submitted":
      return "/onboarding-reviews";
    case "onboarding_decision":
      return "/onboarding/status";
    case "payout_completed":
      return "/wallet";
    case "dispute_update":
      return "/disputes";
    case "dispute_submitted":
      return "/disputes";
    case "bank_change_decision":
      return "/bank-changes";
    case "bank_change_submitted":
      return "/bank-changes";
    case "agent_assignment":
      return "/home";
    case "agent_assigned":
      return "/agents";
    default:
      return null;
  }
}

// The call-to-action label shown on a notification row, paired with its link.
function ctaForNotification(n: Notification): string | null {
  if (n.dataImportId != null) return "View orders";
  switch (n.type) {
    case "onboarding_submitted":
      return "Review application";
    case "onboarding_decision":
      return "View application";
    case "payout_completed":
      return "View wallet";
    case "dispute_update":
      return "View dispute";
    case "dispute_submitted":
      return "Review dispute";
    case "bank_change_decision":
      return "View bank change";
    case "bank_change_submitted":
      return "Review bank change";
    case "agent_assignment":
      return "Go to dashboard";
    case "agent_assigned":
      return "View agents";
    default:
      return null;
  }
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { data: notifications = [], isLoading } = useListNotifications();
  const markRead = useMarkNotificationRead();

  const unread = notifications.filter((n) => !n.readAt).length;

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  async function handleMarkRead(n: Notification) {
    if (n.readAt) return;
    await markRead.mutateAsync({ id: n.id });
    void queryClient.invalidateQueries({
      queryKey: getListNotificationsQueryKey(),
    });
  }

  function handleClick(n: Notification) {
    const link = linkForNotification(n);
    if (link) {
      setOpen(false);
      setLocation(link);
    }
    void handleMarkRead(n);
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-[#0B132B]" />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-[#E6007E] text-white text-[10px] font-bold border-2 border-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white rounded-xl border border-gray-200 shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="font-bold text-sm text-[#0B132B]">
              Notifications
            </span>
            {unread > 0 && (
              <span className="text-xs font-medium text-[#E6007E]">
                {unread} new
              </span>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {isLoading && (
              <div className="px-4 py-8 text-center text-gray-400 text-sm">
                <Loader2 size={16} className="inline animate-spin mr-2" />
                Loading…
              </div>
            )}
            {!isLoading && notifications.length === 0 && (
              <div className="px-4 py-10 text-center text-gray-400 text-sm">
                You're all caught up.
              </div>
            )}
            {notifications.map((n) => {
              const cta = ctaForNotification(n);
              return (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-gray-50 transition-colors ${
                    n.readAt ? "" : "bg-pink-50/40"
                  }`}
                >
                  <div className="mt-0.5 shrink-0">
                    {n.readAt ? (
                      <Check size={16} className="text-gray-300" />
                    ) : (
                      <span className="block w-2 h-2 rounded-full bg-[#E6007E] mt-1.5" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#0B132B]">
                      {n.title}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                      {n.body}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-[11px] text-gray-400">
                        {timeAgo(n.createdAt)}
                      </p>
                      {cta && (
                        <span className="text-[11px] font-medium text-[#E6007E] flex items-center gap-0.5">
                          {cta}
                          <ChevronRight size={12} />
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
